import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Site from 'src/interfaces/api/Site';
import useNewCrmSite from 'src/hooks/api/useSite';
import { Fetchable } from 'src/interfaces/api/api';

interface ContextValue {
  site: Fetchable<Site>;
  addLogo: (file: File) => Promise<string>;
  removeLogo: () => Promise<void>;
}

const Context = React.createContext<ContextValue | undefined>(undefined);

interface ProviderProps {
  legacyCustomerId?: number;
}

export const SiteContextProvider: React.FC<ProviderProps> = (props) => {
  const { children, legacyCustomerId } = props;
  const { getSiteOfLegacyCustomer, addLogo: addSiteLogo, removeLogo: removeSiteLogo } = useNewCrmSite();
  const [site, setSite] = useState<Fetchable<Site>>({ status: 'fetching' });

  const removeLogo = useCallback(async () => {
    if (site.status !== 'fetched' || !site.data?.id) {
      throw new Error('Identifiant du site manquant');
    }

    const data = await removeSiteLogo(site.data.id);

    if (!data.success) {
      throw new Error();
    }
  }, [site]);

  const addLogo = useCallback(
    async (file: File): Promise<string> => {
      if (site.status !== 'fetched' || !site.data?.id) {
        throw new Error('Identifiant du site manquant');
      }

      const data = await addSiteLogo(site.data.id, file);

      if (!data.success) {
        throw new Error();
      }

      return data.documentId;
    },
    [site]
  );

  useEffect(() => {
    let ignore = false;

    if (legacyCustomerId) {
      setSite({ status: 'fetching' });

      getSiteOfLegacyCustomer(legacyCustomerId)
        .then((site) => {
          if (ignore) return;
          setSite({ status: 'fetched', data: site });
        })
        .catch((e) => setSite({ status: 'error', error: new Error(e.message) }));
    }

    return () => {
      ignore = true;
    };
  }, [legacyCustomerId]);

  const contextValue = useMemo<ContextValue>(() => ({ site, addLogo, removeLogo }), [site]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default function useSite(): ContextValue {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('You must wrap components that call `useSite` by a `SiteContextProvider`');
  }

  return context;
}
